/**
 * ``useCite`` — the "+ Citer" insert flow (PR-4b, M2 write path).
 *
 * Inserts a citation chip at the Word caret as a ContentControl, with
 * ``status='pending'`` — the honest two-phase model: the chip claims
 * nothing until the lawyer runs "Vérifier tout" (which calls the BE and
 * flips the status). This is the citation firewall (W7): the add-in never
 * fabricates verification state; it renders a chip from BE-supplied search
 * fields and lets the BE bless it later.
 *
 * **XmlPart write is conditional on an active case (PR-B).** When the doc
 * is bound to a case+brief (``useCaseStore.activeContext``), the insert also
 * mounts the doc-level CustomXmlPart anchor (``ensureMounted``, no-op if
 * already mounted) and mirrors the citation (``upsertCitation``). With NO
 * active context the insert stays ContentControl-only — the CC tag is the
 * persistence anchor (survives close/reopen) and the XmlPart mirror is
 * rebuilt later from CC tags via ``backfillFromChipTags``. We never invent a
 * briefId/caseId — that would be a W4/W7 leak.
 *
 * W1: pure orchestration. The only "decision" is the citation-template
 * jurisdiction/language axis — presentation (which template + script), not a
 * legal judgment — derived single-site from the BE-supplied ``countryCode``
 * + the active brief's ``lang``. No ``family`` (country-code clean-cut, C6).
 */

import { useCallback, useState } from 'react'
import { getContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { renderCitation } from '@/domain/citation/usecases/render'
import { getCitationTemplate } from '@/domain/citation/usecases/get-template'
import { renderPieceLabel } from '@/domain/case-refs/usecases/render-piece'
import type { CitationJurisdiction } from '@/domain/citation/entity'
import { generateChipCid } from '@/domain/citation-chip/usecases/generate-cid'
import {
  deriveChipKind,
  type CitationChip,
  type ChipLanguage,
} from '@/domain/citation-chip/entity'
import type { SourceResult } from '@/domain/sources/entity'
import type { PieceRef } from '@/domain/case-refs/entity'

/** Map a BE country code to the citation domain's jurisdiction token for
 *  template selection. Single-site lookup (NOT a jurisdiction switch on
 *  legal semantics, NOT `family`): only `fr`/`ma` have presets today;
 *  everything else falls through to the FR template via
 *  ``getCitationTemplate``'s own fallback, so we collapse to `'fr'`. */
function jurisOf(countryCode: string): CitationJurisdiction {
  return countryCode.toLowerCase() === 'ma' ? 'ma' : 'fr'
}

export interface UseCiteResult {
  cite: (result: SourceResult) => Promise<void>
  /** Insert a pièce citation (F1 PR-4). The chip's refKey is
   *  ``piece:{exhibitUuid}`` and its text is « Pièce n° N » rendered from
   *  the BE-supplied (party, ordinal). */
  citePiece: (piece: PieceRef) => Promise<void>
  inserting: boolean
  error: Error | null
}

export function useCite(): UseCiteResult {
  const [inserting, setInserting] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Shared insert: build a pending chip from an already-rendered label +
  // refKey, anchor it at the caret (CC), and mirror it into the XmlPart
  // when a case is bound. ``kind`` is derived from the refKey shape
  // (article | jp | piece). W7: status='pending', score 0 — the add-in
  // never fabricates verification; « Vérifier tout » blesses it later.
  const insertChip = useCallback(
    async (refKey: string, renderedText: string, language: ChipLanguage) => {
      setInserting(true)
      setError(null)
      try {
        const active = useCaseStore.getState().activeContext
        const chip: CitationChip = {
          cid: generateChipCid(),
          kind: deriveChipKind(refKey),
          refKey,
          status: 'pending',
          score: 0,
          language,
          insertedAt: new Date().toISOString(),
        }
        const { citationStore, documentMetadata } = getContainer()
        // Bound doc → mount the XmlPart anchor (no-op if already mounted)
        // before inserting, so the doc-level mirror exists for upsert.
        // A mount-then-failed-insert leaves a valid empty-citations anchor —
        // reconcilable (reconcileDrift/backfill), so no rollback needed.
        if (active) {
          await documentMetadata.ensureMounted({
            briefId: active.briefId,
            caseId: active.caseId,
            snapshotVersion: active.snapshotVersion,
            lang: active.lang,
            styleProfile: active.styleProfile,
            lastSyncAt: new Date().toISOString(),
            diffHash: '',
            citations: [],
            bordereau: [],
          })
        }
        await citationStore.insert(chip, renderedText)
        // Mirror into the XmlPart registry (bound docs only). label = the
        // rendered text; codeName/articleNumber are article-only (empty for
        // pièces) and filled by the BE on « Vérifier tout » for articles —
        // the CC tag remains the authoritative source.
        if (active) {
          await documentMetadata.upsertCitation({
            cid: chip.cid,
            refKey: chip.refKey,
            status: chip.status,
            score: chip.score,
            insertedAt: chip.insertedAt,
            label: renderedText,
            codeName: '',
            articleNumber: '',
          })
        }
      } catch (err: unknown) {
        const wrapped = err instanceof Error ? err : new Error(String(err))
        // eslint-disable-next-line no-console
        console.error('[useCite] insert failed', wrapped)
        setError(wrapped)
      } finally {
        setInserting(false)
      }
    },
    [],
  )

  const cite = useCallback(
    async (result: SourceResult) => {
      // C1 / firewall gate: a hit with no BE-built refKey is not citable.
      // Mirrors the web FE precedent (editor/insert-source.ts).
      if (result.refKey === null) return
      const active = useCaseStore.getState().activeContext
      // Brief language drives the chip + template when a case is bound;
      // falls back to 'fr' for an unbound doc. Presentation axis, not a leak.
      const language: ChipLanguage = active?.lang ?? 'fr'
      const template = getCitationTemplate({
        juris: jurisOf(result.countryCode),
        language,
      })
      // Render from refKey alone (label omitted): articles parse the
      // refKey, jurisprudence parses the ECLI into a court label. No
      // resolve-before-insert (plan C5) — the chip is pending.
      const renderedText = renderCitation({ refKey: result.refKey, label: null }, template)
      await insertChip(result.refKey, renderedText, language)
    },
    [insertChip],
  )

  const citePiece = useCallback(
    async (piece: PieceRef) => {
      const active = useCaseStore.getState().activeContext
      const language: ChipLanguage = active?.lang ?? 'fr'
      // refKey = ``piece:{exhibitUuid}`` — activates deriveChipKind's piece
      // branch. The CC tag persists the UUID (not the ordinal), so
      // renumbering the bordereau never corrupts an inserted chip. The
      // displayed « Pièce n° N » comes from the BE-supplied (party, ordinal)
      // — the add-in never computes the number (BE owns the brain, W1).
      const refKey = `piece:${piece.refId}`
      const renderedText = renderPieceLabel(piece.party, piece.ordinal, language)
      await insertChip(refKey, renderedText, language)
    },
    [insertChip],
  )

  return { cite, citePiece, inserting, error }
}
